import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  LIMITE_TEXTO,
  LIMITE_TEXTO_LONGO,
  mascaraCep,
  mascaraCnpj,
  mascaraCpf,
  mascaraTelefone,
} from '@/lib/mascaras';
import {
  agruparPorSecao,
  camposVisiveis,
  limparOcultos,
  validar,
  type CampoDefinicao,
  type FormularioDefinicao,
  type Respostas,
} from '@/lib/formulario';

// Renderizador genérico de formulários.
//
// Não conhece nenhum campo: recebe a definição do backend e desenha o que ela
// descreve. Adicionar uma pergunta ao questionário é inserir uma linha em
// `formulario_campos` — não mexer neste arquivo.

interface Props {
  chave: string;
  condominioId: string;
  aoConcluir?: (requisitos: Array<{ tipo: string; descricao: string }>) => void;
}

interface RespostaDaFuncao {
  definicao?: FormularioDefinicao;
  respostas?: Respostas;
  error?: string;
  erros?: Array<{ campo: string; mensagem: string }>;
  ok?: boolean;
  requisitos?: Array<{ tipo: string; descricao: string }>;
}

// Máscara por tipo: a definição diz o tipo, o renderizador aplica a
// apresentação correspondente. Nenhuma tela precisa saber disso.
const MASCARA: Record<string, (valor: string) => string> = {
  cpf: mascaraCpf,
  cnpj: mascaraCnpj,
  telefone: mascaraTelefone,
  cep: mascaraCep,
};

// Teclado numérico só onde o campo é mesmo numérico. O CNPJ ficou de fora
// porque aceita letra desde a mudança de 2026: forçar o teclado de números
// esconderia metade dos caracteres válidos no celular.
const TECLADO_NUMERICO = new Set(['cpf', 'telefone', 'cep']);

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900';

export default function FormularioDinamico({ chave, condominioId, aoConcluir }: Props) {
  const [definicao, setDefinicao] = useState<FormularioDefinicao | null>(null);
  const [respostas, setRespostas] = useState<Respostas>({});
  const [catalogos, setCatalogos] = useState<
    Record<string, Array<{ valor: string; rotulo: string }>>
  >({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; tipo: 'ok' | 'erro' } | null>(null);

  useEffect(() => {
    let cancelado = false;

    const carregar = async () => {
      setCarregando(true);
      const { data, error } = await supabase.functions.invoke(
        `formulario?chave=${encodeURIComponent(chave)}&condominioId=${encodeURIComponent(condominioId)}`,
        { method: 'GET' },
      );

      if (cancelado) return;
      const resposta = data as RespostaDaFuncao | null;

      if (!resposta?.definicao) {
        setAviso({
          texto: resposta?.error || error?.message || 'Formulário indisponível.',
          tipo: 'erro',
        });
        setCarregando(false);
        return;
      }

      setDefinicao(resposta.definicao);
      setRespostas(resposta.respostas ?? {});

      // Campos que apontam para um catálogo buscam as opções do mesmo endpoint
      // de configuração — que já responde do cache.
      const necessarios = [
        ...new Set(
          resposta.definicao.campos.map((c) => c.origem_opcoes).filter((o): o is string => !!o),
        ),
      ];

      const carregados: Record<string, Array<{ valor: string; rotulo: string }>> = {};
      for (const nome of necessarios) {
        const { data: lista } = await supabase.functions.invoke(`config?chave=${nome}`, {
          method: 'GET',
        });
        if (Array.isArray(lista)) {
          carregados[nome] = lista.map((item: Record<string, unknown>) => ({
            valor: String(item.uf ?? item.id ?? ''),
            rotulo: String(item.nome ?? item.uf ?? item.id ?? ''),
          }));
        }
      }

      if (!cancelado) {
        setCatalogos(carregados);
        setCarregando(false);
      }
    };

    void carregar();
    return () => {
      cancelado = true;
    };
  }, [chave, condominioId]);

  const visiveis = useMemo(
    () => (definicao ? camposVisiveis(definicao, respostas) : []),
    [definicao, respostas],
  );

  const secoes = useMemo(() => agruparPorSecao(visiveis), [visiveis]);

  const alterar = (campo: CampoDefinicao, valor: unknown) => {
    setRespostas((atual) => ({ ...atual, [campo.chave]: valor }));
    setErros((atual) => {
      const { [campo.chave]: _, ...resto } = atual;
      return resto;
    });
  };

  const opcoesDe = (campo: CampoDefinicao) =>
    campo.opcoes ?? (campo.origem_opcoes ? (catalogos[campo.origem_opcoes] ?? []) : []);

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!definicao) return;

    const limpo = limparOcultos(definicao, respostas);
    const locais = validar(definicao, limpo);
    if (Object.keys(locais).length > 0) {
      setErros(locais);
      setAviso({ texto: 'Revise os campos destacados.', tipo: 'erro' });
      return;
    }

    setSalvando(true);
    setAviso(null);

    const { data, error } = await supabase.functions.invoke('formulario', {
      body: { chave, condominioId, respostas: limpo },
    });
    const resposta = data as RespostaDaFuncao | null;
    setSalvando(false);

    // O servidor revalida: se ele recusar, é a resposta dele que aparece.
    if (resposta?.erros) {
      setErros(Object.fromEntries(resposta.erros.map((e) => [e.campo, e.mensagem])));
      setAviso({ texto: 'O servidor recusou algumas respostas.', tipo: 'erro' });
      return;
    }

    if (!resposta?.ok) {
      setAviso({
        texto: resposta?.error || error?.message || 'Não foi possível salvar.',
        tipo: 'erro',
      });
      return;
    }

    const quantos = resposta.requisitos?.length ?? 0;
    setAviso({ texto: `Respostas salvas. ${quantos} requisito(s) aplicáveis.`, tipo: 'ok' });
    aoConcluir?.(resposta.requisitos ?? []);
  };

  if (carregando) {
    return (
      <div className="py-10 text-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        <p className="text-sm text-foreground-500 mt-2">Carregando formulário...</p>
      </div>
    );
  }

  if (!definicao) {
    return (
      <div className="px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
        {aviso?.texto || 'Formulário indisponível.'}
      </div>
    );
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-6">
      <div>
        <h2 className="text-base font-semibold text-foreground-900">{definicao.titulo}</h2>
        {definicao.descricao ? (
          <p className="text-sm text-foreground-500 mt-1">{definicao.descricao}</p>
        ) : null}
      </div>

      {secoes.map(([secao, campos]) => (
        <fieldset key={secao} className="border border-background-200/70 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-foreground-700">{secao}</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            {campos.map((campo) => (
              <div key={campo.chave} className={campo.tipo === 'textarea' ? 'md:col-span-2' : ''}>
                <label
                  htmlFor={campo.chave}
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  {campo.rotulo}
                  {campo.obrigatorio ? <span className="text-red-600"> *</span> : null}
                </label>

                {campo.tipo === 'booleano' ? (
                  <div className="flex items-center gap-2 h-[42px]">
                    <input
                      id={campo.chave}
                      type="checkbox"
                      checked={respostas[campo.chave] === true}
                      onChange={(e) => alterar(campo, e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground-600">Sim</span>
                  </div>
                ) : campo.tipo === 'select' ? (
                  <select
                    id={campo.chave}
                    value={String(respostas[campo.chave] ?? '')}
                    onChange={(e) => alterar(campo, e.target.value)}
                    className={CLASSE_CAMPO}
                  >
                    <option value="">Selecione...</option>
                    {opcoesDe(campo).map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </select>
                ) : campo.tipo === 'textarea' ? (
                  <textarea
                    id={campo.chave}
                    rows={3}
                    maxLength={Number(campo.validacoes?.maxLength ?? LIMITE_TEXTO_LONGO)}
                    value={String(respostas[campo.chave] ?? '')}
                    onChange={(e) => alterar(campo, e.target.value)}
                    className={CLASSE_CAMPO}
                  />
                ) : (
                  <input
                    id={campo.chave}
                    type={
                      campo.tipo === 'numero'
                        ? 'number'
                        : campo.tipo === 'data'
                          ? 'date'
                          : campo.tipo === 'email'
                            ? 'email'
                            : 'text'
                    }
                    inputMode={TECLADO_NUMERICO.has(campo.tipo) ? 'numeric' : undefined}
                    autoCapitalize={campo.tipo === 'cnpj' ? 'characters' : undefined}
                    maxLength={
                      campo.tipo === 'numero' || campo.tipo === 'data'
                        ? undefined
                        : Number(campo.validacoes?.maxLength ?? LIMITE_TEXTO)
                    }
                    value={String(respostas[campo.chave] ?? '')}
                    onChange={(e) => {
                      const bruto = e.target.value;
                      const mascara = MASCARA[campo.tipo];
                      alterar(
                        campo,
                        campo.tipo === 'numero' ? Number(bruto) : mascara ? mascara(bruto) : bruto,
                      );
                    }}
                    className={CLASSE_CAMPO}
                  />
                )}

                {campo.ajuda ? (
                  <p className="text-xs text-foreground-500 mt-1">{campo.ajuda}</p>
                ) : null}
                {erros[campo.chave] ? (
                  <p className="text-xs text-red-600 mt-1">{erros[campo.chave]}</p>
                ) : null}
              </div>
            ))}
          </div>
        </fieldset>
      ))}

      {aviso ? (
        <div
          className={`px-4 py-3 rounded-md text-sm border ${
            aviso.tipo === 'ok'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {aviso.texto}
        </div>
      ) : null}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={salvando}
          className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
        >
          {salvando ? 'Salvando...' : 'Salvar respostas'}
        </button>
      </div>
    </form>
  );
}
